<!--
출고운임 과금 - 인라인 UI
------------------------------------
환율 입력 → 과금 디스패치 → 폴링 → 완료/실패 표시
페이지 재방문 시 기존 진행중인 작업이 있으면 자동 폴링 시작
-->
<template>
    <div>
        <!-- TASK-063 (2026-04-28): 안내 메시지 디자인 보강
             - 좌우 패딩 22px (하단 "과금완료 내역" 카드와 정렬 일치)
             - 좌측 4px accent + 옅은 초록 배경 (완료 케이스) / 회색 (레거시 케이스)
             - 둥근 모서리 + 1px 외곽선으로 카드 영역과 동일 톤 -->
        <div v-if="jobState === 'ready' && !hasUncharged" class="uk-margin-bottom"
             :style="hasCompletedBillings
                ? 'padding:14px 22px;background:#f1f8e9;border:1px solid #c5e1a5;border-left:4px solid #2e7d32;border-radius:6px'
                : 'padding:14px 22px;background:#fafafa;border:1px solid #e0e0e0;border-left:4px solid #9e9e9e;border-radius:6px'">
            <span v-if="hasCompletedBillings" style="color:#2e7d32;font-weight:600;font-size:1.05rem">
                <i class="material-icons" style="font-size:20px;vertical-align:middle;color:#2e7d32">check_circle</i>
                모든 하우스의 출고운임 과금이 완료되었습니다.
            </span>
            <span v-else style="color:#555;font-weight:500;font-size:1rem">
                <i class="material-icons" style="font-size:20px;vertical-align:middle;color:#888">info</i>
                과금기능 변경 전 데이터입니다. 예치금으로 처리되었습니다.
            </span>
        </div>

        <!-- 진행 케이스 (환율입력 / 대기 / 실행중 / 완료직후 / 실패): 카드 wrapper -->
        <div v-else class="uk-card uk-card-default uk-card-body uk-card-small uk-margin-bottom">

        <!-- 대기화면: 환율 입력 (REF-015 2026-04-27 디자인 리팩토링)
             폼 레이아웃: 가운데 정렬 + 입력 + 버튼 인접 배치 + 항목별 박스 구분 + 가독성 강화 -->
        <div v-show="jobState === 'ready' && hasUncharged">
            <!-- 정보 박스 그리드 (회원사/하우스 + 통화/출고일) -->
            <div style="display:flex;flex-wrap:wrap;border:1px solid #e0e0e0;border-radius:4px;overflow:hidden;margin-bottom:14px">
                <div style="flex:1;min-width:120px;padding:10px 16px;border-right:1px solid #e0e0e0;background:#fafafa">
                    <div class="uk-text-meta" style="font-size:1rem">과금대상 회원사</div>
                    <div class="uk-text-bold" style="font-size:1.125rem">{{ unchargedUserCount }}</div>
                </div>
                <div style="flex:1;min-width:120px;padding:10px 16px;border-right:1px solid #e0e0e0;background:#fafafa">
                    <div class="uk-text-meta" style="font-size:1rem">과금대상 하우스</div>
                    <div class="uk-text-bold" style="font-size:1.125rem">{{ unchargedHouseCount }}건</div>
                </div>
                <div style="flex:1;min-width:120px;padding:10px 16px;border-right:1px solid #e0e0e0">
                    <div class="uk-text-meta" style="font-size:1rem">출고일</div>
                    <div class="uk-text-bold" style="font-size:1.125rem">{{ shipoutDate }}</div>
                </div>
                <div style="flex:1;min-width:120px;padding:10px 16px">
                    <div class="uk-text-meta" style="font-size:1rem">지역통화</div>
                    <div class="uk-text-bold" style="font-size:1.125rem">{{ regionCurrency }}</div>
                </div>
            </div>
            <!-- 환율 입력 + 과금시작 버튼 (가운데 정렬, 인접 배치) -->
            <div class="uk-flex uk-flex-center uk-flex-middle" style="gap:12px;padding:14px 0;background:#f5f9ff;border:1px solid #cfe1f5;border-radius:4px">
                <span class="uk-text-bold" style="font-size:1.125rem;color:#1565c0">적용환율</span>
                <app-input-text
                        inputSize="medium"
                        :placeholder="'1 '+regionCurrency+' to KRW'"
                        v-model.trim="regionToKrwRate"
                        style="width: 220px; display: inline-block"
                ></app-input-text>
                <button
                    class="uk-button uk-button-primary"
                    type="button"
                    style="font-size:1.05rem;padding:0 20px"
                    v-on:click="jobStart()"
                >
                    <span uk-icon="icon: play; ratio: 1"></span>
                    출고운임 과금시작
                </button>
            </div>
            <div class="uk-text-danger uk-margin-small-top" style="font-size:1rem;text-align:center">
                <i class="material-icons uk-text-middle" style="font-size: 18px">warning</i>
                환율 입력시, 타 국가의 환율을 입력하지 않도록 주의하세요!
            </div>
        </div>

        <!-- 과금기능 변경 전 데이터 (미과금 없음 + 과금 완료 billing도 없음 = 레거시) -->
        <div v-show="jobState === 'ready' && !hasUncharged && !hasCompletedBillings">
            <div class="uk-flex uk-flex-middle">
                <i class="material-icons uk-text-middle uk-text-muted" style="font-size: 22px">info</i>
                <span class="uk-text-muted uk-margin-small-left">과금기능 변경 전 데이터입니다. 예치금으로 처리되었습니다.</span>
            </div>
        </div>

        <!-- 과금 완료 (미과금 없음 + 과금 완료 billing 있음) -->
        <div v-show="jobState === 'ready' && !hasUncharged && hasCompletedBillings">
            <div class="uk-flex uk-flex-middle">
                <i class="material-icons uk-text-middle uk-text-success" style="font-size: 22px">check_circle</i>
                <span class="uk-text-muted uk-margin-small-left">모든 하우스의 출고운임 과금이 완료되었습니다.</span>
            </div>
        </div>

        <!-- 대기중 (Job이 큐에 등록됨, Worker 픽업 전) -->
        <div v-show="jobState === 'pending'">
            <div class="uk-flex uk-flex-middle">
                <div uk-spinner="ratio: 0.6" class="uk-margin-small-right"></div>
                <span class="uk-text-primary">과금 작업이 대기중입니다...</span>
                <span class="uk-text-muted uk-margin-small-left">서버에서 작업을 준비하고 있습니다.</span>
            </div>
            <progress class="uk-progress uk-margin-small-top uk-margin-remove-bottom" :value="processedCount" :max="totalCount || 1"></progress>
        </div>

        <!-- 실행중 (Worker가 Job을 픽업하여 처리중) -->
        <div v-show="jobState === 'doing'">
            <div class="uk-flex uk-flex-middle uk-flex-between">
                <div class="uk-flex uk-flex-middle">
                    <div uk-spinner="ratio: 0.6" class="uk-margin-small-right"></div>
                    <span class="uk-text-primary">서버에서 과금처리 중입니다...</span>
                    <span class="uk-text-muted uk-margin-small-left">브라우저를 닫아도 서버에서 처리가 계속됩니다.</span>
                </div>
                <div class="uk-text-large uk-text-primary">
                    {{ processedCount }} / {{ totalCount }}
                </div>
            </div>
            <progress class="uk-progress uk-margin-small-top uk-margin-remove-bottom" :value="processedCount" :max="totalCount || 1"></progress>
        </div>

        <!-- 완료 (잠시 표시 후 페이지 새로고침) -->
        <div v-show="jobState === 'done'">
            <div class="uk-flex uk-flex-middle">
                <i class="material-icons uk-text-middle uk-text-success" style="font-size: 28px">check_circle</i>
                <span class="uk-text-primary uk-text-bold uk-margin-small-left">과금이 완료되었습니다. 페이지를 갱신합니다...</span>
            </div>
        </div>

        <!-- 실패 -->
        <div v-show="jobState === 'failed'">
            <div class="uk-flex uk-flex-middle uk-flex-between">
                <div class="uk-flex uk-flex-middle">
                    <i class="material-icons uk-text-middle uk-text-danger" style="font-size: 28px">error</i>
                    <span class="uk-text-danger uk-text-bold uk-margin-small-left">과금 작업이 실패했습니다</span>
                </div>
                <div>
                    <button
                        v-show="hasUncharged"
                        class="uk-button uk-button-primary uk-button-small uk-margin-small-right"
                        type="button"
                        @click="jobRetry"
                    >
                        <i class="material-icons uk-text-middle" style="font-size: 18px">replay</i>
                        재과금 시도
                    </button>
                    <button
                        class="uk-button uk-button-default uk-button-small"
                        type="button"
                        @click="refreshPage"
                    >
                        <i class="material-icons uk-text-middle" style="font-size: 18px">refresh</i>
                        새로고침
                    </button>
                </div>
            </div>
            <div v-if="errorMessage" class="uk-margin-small-top">
                <div class="uk-text-small uk-text-muted" style="max-height: 100px; overflow-y: auto; padding: 8px; background: #f8f8f8; border-radius: 4px;">
                    {{ errorMessage }}
                </div>
            </div>
        </div>

        </div><!-- /진행 케이스 카드 wrapper -->
    </div><!-- /single root -->
</template>

<script>
    export default {
        props: {
            // csrf-token
            csrfToken: String,
            /**
             * 지역id
             */
            regionId: {
                type: Number,
                default: null,
            },
            /**
             * 출고id
             */
            shipoutId: {
                type: Number,
                default: null,
            },
            /**
             * 출고일
             */
            shipoutDate: {
                type: String,
                default: null,
            },
            /**
             * 지역통화
             */
            regionCurrency: {
                type: String,
                default: null,
            },
            /**
             * 진행중인 progress ID (PENDING/RUNNING만 — 재방문 시 폴링 복구용)
             */
            activeProgressId: {
                type: Number,
                default: null,
            },
            /**
             * 진행중 progress 상태값 (1=PENDING, 2=RUNNING)
             */
            activeProgressStatus: {
                type: Number,
                default: null,
            },
            /**
             * 미과금 하우스 존재 여부
             */
            hasUncharged: {
                type: Boolean,
                default: true,
            },
            /**
             * 과금 완료 billing이 존재하는지 (레거시 데이터 분기용)
             */
            hasCompletedBillings: {
                type: Boolean,
                default: false,
            },
            /**
             * R-26: 미과금 회원사 수
             */
            unchargedUserCount: {
                type: Number,
                default: 0,
            },
            /**
             * R-26: 미과금 하우스 수
             */
            unchargedHouseCount: {
                type: Number,
                default: 0,
            },
        },
        computed: {
        },
        data: function () {

            return {
                // 입력한 환율
                regionToKrwRate: null,

                /**
                 * 현재 작업진행상태
                 *  - ready   : 환율 입력 대기 (초기 화면)
                 *  - pending : 작업 대기중 (Job 큐 등록됨, Worker 픽업 전)
                 *  - doing   : 실행중 (Worker가 Job을 픽업하여 처리중)
                 *  - done    : 완료 (All-or-Nothing 전체 성공)
                 *  - failed  : 실패
                 */
                jobState: 'ready',

                // 폴링용 데이터
                progressId: null,
                pollTimer: null,
                totalCount: 0,
                processedCount: 0,
                errorMessage: null,

            };
        },
        mounted() {
            // 재방문 시 진행중인 작업이 있으면 폴링 시작 (PENDING/RUNNING만)
            if (this.activeProgressId) {
                this.progressId = this.activeProgressId;
                this.jobState = this.activeProgressStatus === 2 ? 'doing' : 'pending';
                this.startPolling();
            }
            // COMPLETED/FAILED는 조회하지 않음 — 과금완료 이력은 Blade에서 과금상세 링크로 제공
        },
        methods: {
            // 작업시작 → 디스패치 API 1회 호출
            jobStart() {

                // 환율 검증
                if (! this.regionToKrwRate || parseFloat(this.regionToKrwRate) <= 0) {
                    alert('환율을 입력하세요 (0보다 큰 값)');
                    return;
                }

                // 컨펌
                if (! confirm('한번 과금되면 취소할 수 없습니다. 입력한 환율('+this.regionToKrwRate+') 이 정확합니까?')) {
                    return;
                }

                // 진행상태 → 대기중 (디스패치 후 Worker 픽업 전)
                this.jobState = 'pending';

                // 디스패치 API 호출
                let cmp = this;
                let opts = {
                    type: 'post',
                    url: '/regions/'+this.regionId+'/shipout-cost-not-charged-shipouts/'+this.shipoutId+'/dispatch-charge',
                    data: {
                        '_token': cmp.csrfToken,
                        'region_to_krw_rate': cmp.regionToKrwRate,
                    },
                    success: function (response) {
                        cmp.progressId = response.progress_id;
                        // 폴링 시작
                        cmp.startPolling();
                    },
                    alert_error_msg: function(msg){
                        cmp.jobState = 'failed';
                        cmp.errorMessage = msg;
                    },
                };
                $.app.ajax.module.default(opts);
            },

            // 폴링 시작
            startPolling() {
                let cmp = this;
                this.pollTimer = setInterval(function() {
                    cmp.pollProgress();
                }, 2000);
            },

            // 폴링 중지
            stopPolling() {
                if (this.pollTimer) {
                    clearInterval(this.pollTimer);
                    this.pollTimer = null;
                }
            },

            // 진행현황 폴링 (background: 로딩 UI 깜빡임 방지)
            pollProgress() {
                let cmp = this;
                let opts = {
                    type: 'get',
                    url: '/batch-job-progresses/'+this.progressId,
                    success: function (response) {
                        cmp.totalCount = response.total_count;
                        cmp.processedCount = response.processed_count;

                        // 상태별 처리 (PENDING=1, RUNNING=2, COMPLETED=3, FAILED=9)
                        if (response.status === 1) {
                            // 대기중
                            cmp.jobState = 'pending';
                        } else if (response.status === 2) {
                            // 실행중
                            cmp.jobState = 'doing';
                        } else if (response.status === 3) {
                            // 완료 → 페이지 새로고침 (미과금 목록 갱신)
                            cmp.stopPolling();
                            cmp.jobState = 'done';
                            setTimeout(function() { window.location.reload(); }, 1500);
                        } else if (response.status === 9) {
                            // 실패
                            cmp.stopPolling();
                            cmp.jobState = 'failed';
                            cmp.errorMessage = response.error_message;
                        }
                    },
                    error: function(){
                        // 폴링 에러 시에는 계속 시도 (네트워크 일시 장애 등)
                    },
                };
                $.app.ajax.module.background(opts);
            },

            // 재과금 시도 (실패 후 ready 상태로 복귀)
            jobRetry() {
                this.jobState = 'ready';
                this.progressId = null;
                this.totalCount = 0;
                this.processedCount = 0;
                this.errorMessage = null;
                // 환율은 유지 (사용자 편의)
            },

            // 현재페이지 갱신
            refreshPage() {
                $.app.refresh();
            },

            // 과금페이지로 이동
            goToChargePage() {
                $.app.move('/regions/'+this.regionId+'/shipout-cost-not-charged-shipouts');
            },

            // 숫자 포맷팅 (천 단위 쉼표)
            formatNumber(value) {
                if (value === null || value === undefined) return '0';
                return Number(value).toLocaleString('ko-KR');
            },

        },
        beforeDestroy() {
            this.stopPolling();
        },
    }
</script>

<style lang="scss" scoped>
</style>
